import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import GenericApi from "@/api/genericApi";
import {
  BankAccountQueryKey,
  BankFileQueryKey,
  CoPQueryKey,
  CryptoAccountQueryKey,
  GenericStatusQueryKey,
} from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiError, ApiResponse } from "@/types/Api";
import {
  BankAccount,
  BankAccountCreate,
  BankFile,
  BankResponse,
  CryptoAccount,
  CryptoAccountResponse,
  EditBankStatus,
} from "@/types/BankSettings";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Loader from "@/components/Loader";
import { useSnackBarAlert } from "@/hooks/useSnackbar";
import { ValidationErrors } from "@/types/ValidationError";
import CustomLogger from "@/utils/CustomLogger";
import { getUser, getUserToken } from "@/utils/localStorage";
import axios from "axios";
import { NETWORK_OPTIONS } from "@/data/data";
import { RootState } from "@/store";
import { useSelector } from "react-redux";
import { checkPermissionByKeyAndValue, isBitramp } from "@/utils/utils";
import { AnimatePresence, motion } from "framer-motion";
import genericApi from "@/api/genericApi";
// import { set } from "zod";
import { Button } from "@/components/UI/Button";

const BankCryptoManagement: React.FC = () => {
  const queryClient = useQueryClient();

  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const currencyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        currencyRef.current &&
        !currencyRef.current.contains(event.target as Node)
      ) {
        setCurrencyDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const localUser = getUser();

  const handleCurrencySelect = (value: string) => {
    setNewBankAccount((prev) => ({
      ...prev,
      currency: value,
    }));
    setCurrencyDropdownOpen(false);
  };

  const [networkDropdownOpen, setNetworkDropdownOpen] = useState(false);
  const networkRef = useRef<HTMLDivElement>(null);

  const handleNetworkSelect = (value: string) => {
    setNewCryptoWallet((prev) => ({
      ...prev,
      network: value,
    }));
    setNetworkDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        networkRef.current &&
        !networkRef.current.contains(event.target as Node)
      ) {
        setNetworkDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [currencyCryptoDropdownOpen, setCurrencyCryptoDropdownOpen] =
    useState(false);
  const currencyCryptoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        currencyCryptoRef.current &&
        !currencyCryptoRef.current.contains(event.target as Node)
      ) {
        setCurrencyCryptoDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCurrencyCryptoSelect = (value: string) => {
    setNewCryptoWallet((prev) => ({
      ...prev,
      currency: value,
    }));
    setCurrencyCryptoDropdownOpen(false);
  };

  const [showAddBankForm, setShowAddBankForm] = useState(false);
  const [statement, setStatement] = useState<File | null>(null);
  const [fileName, setFileName] = useState("No file chosen");
  const [idForMutate, setIdForMutate] = useState<number | null>(null);
  const [actionForModal, setActionForModal] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [walletAddressExample, setWalletAddressExample] = useState<string>("");

  const { selectedClient } = useSelector((state: RootState) => state.client);
  const user = getUser();

  const [showEditBankForm, setShowEditBankForm] = useState<number | null>(null);
  const [showEditCryptoForm, setShowEditCryptoForm] = useState<number | null>(
    null
  );

  const [editBankStatus, setEditBankStatus] = useState<EditBankStatus>({
    status: "pending",
    decline_reason: "",
    wallet_id: 0,
  });

  const getFileName = (file: string | null) => {
    return file ? file.split("/").pop() : "No file uploaded";
  };

  const handleCloseModal = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setShowConfirmModal(false);
    }
  };

  const [newBankAccount, setNewBankAccount] = useState({
    name: "",
    owner_name: "",
    iban: "",
    bank_name: "",
    swift: "",
    currency: "EUR",
  });

  const [editedBankAccount, setEditedBankAccount] = useState({
    name: "",
    owner_name: "",
    iban: "",
    bank_name: "",
    swift: "",
    currency: "EUR",
    id: 0,
    status: {
      status: "",
      decline_reason: "",
      last_updated: "",
    },
  });

  const populateEditBankData = (account: BankAccount) => {
    setEditedBankAccount({
      name: account.name,
      owner_name: account.owner_name,
      iban: account.iban,
      bank_name: account.bank_name,
      swift: account.swift,
      currency: account.currency,
      id: account.id,
      status: account.status,
    });
  };

  const [editedCryptoAccountData, setEditedCryptoData] = useState({
    name: "",
    wallet_address: "",
    network: "",
    currency: "",
    id: 0,
    status: {
      status: "",
      decline_reason: "",
      last_updated: "",
    },
  });

  const populateEditCryptoData = (account: CryptoAccount) => {
    setEditedCryptoData({
      name: account.name,
      wallet_address: account.wallet_address,
      network: account.network,
      currency: account.currency,
      id: account.id,
      status: account.status,
    });
  };

  const [showAddCryptoForm, setShowAddCryptoForm] = useState(false);
  const [newCryptoWallet, setNewCryptoWallet] = useState({
    name: "",
    wallet_address: "",
    network: "TRC20",
    currency: "ETH",
  });

  useEffect(() => {
    if (newCryptoWallet.currency == "BTC") {
      setWalletAddressExample("bc1qw508d6qe3n4ceq64qv2r0td33h8x9r4kczg3vq");
    }
    if (newCryptoWallet.currency == "ETH") {
      setWalletAddressExample("0xde0B295669a9FD93d5F28D9Ec85E40f4cb697BAe");
    }
    if (newCryptoWallet.currency == "USDT") {
      setWalletAddressExample("0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48");
    }
    if (newCryptoWallet.currency == "USDC") {
      setWalletAddressExample("TQXjXpbJw9YZxKWhqQFHRdTxxvEiAGYyLR");
    }
  }, [newCryptoWallet.currency]);

  // Bank account handlers

  const handleAddBankAccount = () => {
    if (statement === null) {
      showSnackBar(
        "File is required",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
      return;
    }
    addBankAccountMutation.mutate({
      name: newBankAccount.name,
      owner_name: newBankAccount.owner_name,
      iban: newBankAccount.iban,
      bank_name: newBankAccount.bank_name,
      swift: newBankAccount.swift,
      currency: newBankAccount.currency,
      statement_verified: true,
    });

    setShowAddBankForm(false);
  };

  const handleUpdateBankStatus = (data: EditBankStatus) => {
    editBankAccountStatusMutation.mutate({
      status: data.status,
      decline_reason: data.decline_reason,
      wallet_id: data.wallet_id,
    });
  };

  const handleUpdateCryptoStatus = (data: EditBankStatus) => {
    editCryptoAccountStatusMutation.mutate({
      status: data.status,
      decline_reason: data.decline_reason,
      wallet_id: data.wallet_id,
    });
  };

  const handleDeleteCryptoWallet = async (id: number) => {
    await GenericApi.delete(`${ApiRoutes.CRYPTOACCOUNT}/${id}`);
    queryClient.invalidateQueries({ queryKey: [CryptoAccountQueryKey] });
  };

  const handleAddCryptoWallet = () => {
    addCryptoAccountMutation.mutate({
      name: newCryptoWallet.name,
      wallet_address: newCryptoWallet.wallet_address,
      network: newCryptoWallet.network,
      currency: newCryptoWallet.currency,
    });
    setNewCryptoWallet({
      name: "",
      wallet_address: "",
      network: "TRC20",
      currency: "ETH",
    });
    setShowAddCryptoForm(false);
  };

  const { data: bankAccounts, isLoading: bankAccountsLoading } = useQuery<
    ApiResponse<BankAccount[]>
  >({
    queryFn: async () =>
      GenericApi.get<BankAccount[]>(
        selectedClient
          ? `${ApiRoutes.BANKACCOUNT}/user/${selectedClient?.id}`
          : ApiRoutes.BANKACCOUNT
      ),
    queryKey: [BankAccountQueryKey],
  });

  const addBankAccountMutation = useMutation<
    ApiResponse<BankResponse>,
    ApiError<ValidationErrors>,
    BankAccountCreate
  >({
    mutationFn: (data) => {
      return GenericApi.post<BankResponse>(
        selectedClient
          ? `${ApiRoutes.BANKACCOUNT}?user_id=${selectedClient.id}`
          : ` ${ApiRoutes.BANKACCOUNT}`,
        {
          ...data,
        }
      );
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      if (data?.id) {
        addBankFile.mutate({
          statement: statement,
          bank_id: data.id,
        });
      }
      showSnackBar(
        "Bank Account Added",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
      queryClient.invalidateQueries({ queryKey: [BankAccountQueryKey] });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      setShowAddBankForm(false);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      if (axios.isAxiosError(error)) {
        console.log("Eroare detaliată: ", error.response?.data);
      } else {
        console.log("Eroare detaliată: ", error?.message);
      }

      let validationMessages = "Unknown error";

      if (axios.isAxiosError(error)) {
        const details = error.response?.data?.detail;

        if (Array.isArray(details)) {
          validationMessages = details
            .map((d: { msg?: string }) => d?.msg || JSON.stringify(d))
            .join("; ");
        } else if (typeof error.response?.data?.message === "string") {
          validationMessages = error.response.data.message;
        } else if (typeof error.message === "string") {
          validationMessages = error.message;
        }
      }

      showSnackBar(
        validationMessages,
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
    },
  });

  const editBankAccountMutation = useMutation<
    ApiResponse<BankAccount>,
    ApiError<ValidationErrors>,
    Partial<BankAccount>
  >({
    mutationFn: (data) => {
      return GenericApi.put<BankAccount>(
        `${ApiRoutes.BANKACCOUNT}/${data.id}`,
        {
          name: editedBankAccount.name,
          owner_name: editedBankAccount.owner_name,
          iban: editedBankAccount.iban,
          bank_name: editedBankAccount.bank_name,
          swift: editedBankAccount.swift,
          currency: editedBankAccount.currency,
          status: editedBankAccount.status,
        }
      );
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      showSnackBar(
        "Bank Account Updated",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
      queryClient.invalidateQueries({ queryKey: [BankAccountQueryKey] });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      setShowEditBankForm(null);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      showSnackBar(
        `${error?.name}`,
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
    },
  });

  const editBankAccountStatusMutation = useMutation<
    ApiResponse<EditBankStatus>,
    ApiError<ValidationErrors>,
    EditBankStatus
  >({
    mutationFn: (data) => {
      return GenericApi.put<EditBankStatus>(
        `${ApiRoutes.BANKACCOUNT}/${data.wallet_id}`,
        {
          status: {
            status: data.status,
            decline_reason: data.decline_reason,
          },
        }
      );
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      showSnackBar(
        "Bank Account Status Updated",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
      queryClient.invalidateQueries({ queryKey: [BankAccountQueryKey] });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      setEditBankStatus({
        status: "pending",
        decline_reason: "",
        wallet_id: 0,
      });
      setShowEditBankForm(null);
      setShowConfirmModal(false);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      showSnackBar(
        `${error?.name}`,
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
    },
  });

  const { data: cryptoAccounts, isLoading: cryptoAccountsLoading } = useQuery<
    ApiResponse<CryptoAccount[]>
  >({
    queryFn: async () =>
      GenericApi.get<CryptoAccount[]>(
        user?.is_staff
          ? `${ApiRoutes.CRYPTOACCOUNT}/user/${selectedClient?.id}`
          : ApiRoutes.CRYPTOACCOUNT
      ),
    queryKey: [CryptoAccountQueryKey],
  });

  const editCryptoAccountMutation = useMutation<
    ApiResponse<CryptoAccount>,
    ApiError<ValidationErrors>,
    Partial<CryptoAccount>
  >({
    mutationFn: (data) => {
      return GenericApi.put<CryptoAccount>(
        `${ApiRoutes.CRYPTOACCOUNT}/${data.id}`,
        {
          name: editedCryptoAccountData.name,
          wallet_address: editedCryptoAccountData.wallet_address,
          network: editedCryptoAccountData.network,
          currency: editedCryptoAccountData.currency,
          status: editedCryptoAccountData.status,
        }
      );
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      showSnackBar(
        "Crypto Wallet Updated",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
      queryClient.invalidateQueries({ queryKey: [CryptoAccountQueryKey] });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      setShowEditCryptoForm(null);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      showSnackBar(
        `${error?.name}`,
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
    },
  });

  const editCryptoAccountStatusMutation = useMutation<
    ApiResponse<EditBankStatus>,
    ApiError<ValidationErrors>,
    EditBankStatus
  >({
    mutationFn: (data) => {
      return GenericApi.put<EditBankStatus>(
        `${ApiRoutes.CRYPTOACCOUNT}/${data.wallet_id}`,
        {
          status: {
            status: data.status,
            decline_reason: data.decline_reason,
          },
        }
      );
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      showSnackBar(
        "Crypto Account Status Updated",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
      queryClient.invalidateQueries({ queryKey: [CryptoAccountQueryKey] });
      setEditBankStatus({
        status: "pending",
        decline_reason: "",
        wallet_id: 0,
      });
      setShowEditCryptoForm(null);
      setShowConfirmModal(false);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      showSnackBar(
        `${error?.name}`,
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
    },
  });

  const addCryptoAccountMutation = useMutation<
    ApiResponse<CryptoAccountResponse>,
    ApiError<ValidationErrors>,
    CryptoAccountResponse
  >({
    mutationFn: (data) => {
      return GenericApi.post<CryptoAccountResponse>(
        selectedClient
          ? `${ApiRoutes.CRYPTOACCOUNT}?user_id=${selectedClient?.id}`
          : ApiRoutes.CRYPTOACCOUNT,
        {
          ...data,
        }
      );
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      showSnackBar(
        "Crypto wallet Added",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
      queryClient.invalidateQueries({ queryKey: [CryptoAccountQueryKey] });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      setShowAddBankForm(false);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      if (axios.isAxiosError(error)) {
        console.log("Eroare detaliată: ", error.response?.data);
      } else {
        console.log("Eroare detaliată: ", error?.message);
      }

      let validationMessages = "Unknown error";

      if (axios.isAxiosError(error)) {
        const details = error.response?.data?.detail;

        if (Array.isArray(details)) {
          validationMessages = details
            .map((d: { msg?: string }) => d?.msg || JSON.stringify(d))
            .join("; ");
        } else if (typeof error.response?.data?.message === "string") {
          validationMessages = error.response.data.message;
        } else if (typeof error.message === "string") {
          validationMessages = error.message;
        }
      }

      showSnackBar(
        validationMessages,
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
    },
  });

  const handleDeleteBankAccount = async (id: number) => {
    await GenericApi.delete(`${ApiRoutes.BANKACCOUNT}/${id}`);
    queryClient.invalidateQueries({ queryKey: [BankAccountQueryKey] });
  };

  const { showSnackBar } = useSnackBarAlert();

  const validatePdfFile = (file: File | null) => {
    if (!file) {
      showSnackBar(
        "File is required",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
      return false;
    }

    return true;
  };

  const handleStatementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isValid = validatePdfFile(e.target.files ? e.target.files[0] : null);
    if (!isValid) return;

    setStatement(e.target.files ? e.target.files[0] : null);
    setFileName(e.target.files ? e.target.files[0].name : "No file chosen");
  };

  const addBankFile = useMutation<
    ApiResponse<BankFile>,
    ApiError<ValidationErrors>,
    BankFile
  >({
    mutationFn: async (data) => {
      const formData = new FormData();
      formData.append("statement", statement!);
      const response = await axios.post<ApiResponse<BankFile>>(
        `${genericApi.baseUrl}${ApiRoutes.BANKACCOUNT}/${data.bank_id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${getUserToken()}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({ queryKey: [BankFileQueryKey] });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      setStatement(null);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      if (axios.isAxiosError(error)) {
        console.log("Eroare detaliată: ", error.response?.data);
      } else {
        console.log("Eroare detaliată: ", error?.message);
      }

      let validationMessages = "Unknown error";

      if (axios.isAxiosError(error)) {
        const details = error.response?.data?.detail;

        if (Array.isArray(details)) {
          validationMessages = details
            .map((d: { msg?: string }) => d?.msg || JSON.stringify(d))
            .join("; ");
        } else if (typeof error.response?.data?.message === "string") {
          validationMessages = error.response.data.message;
        } else {
          validationMessages = error.message;
        }
      }

      showSnackBar(
        validationMessages,
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
    },
  });

  const canEditQuestionary =
    checkPermissionByKeyAndValue(
      user?.permissions || [],
      "change",
      "financialinfo"
    ) || user?.is_superuser;

  const isDisabled = !(canEditQuestionary || !user?.is_staff);

  console.log("bankAccounts", bankAccountsLoading, cryptoAccountsLoading);

  if (bankAccountsLoading || cryptoAccountsLoading) {
    return <Loader />;
  }

  return (
    <div className="mx-auto p-8 Container !rounded-tr-[0rem] pshadow Text">
      {/* Bank Account Section */}
      <div className="settings pt-2 mb-3">
        <div className="flex gap-4 items-center mb-10 ">
          <div
            className={`w-fit whitespace-nowrap semi-bold Text dark-blue ${
              isBitramp() ? "text-white" : ""
            }`}
          >
            Bank Account
          </div>
          {isBitramp() ? (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
          ) : (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#f1f1f1] rounded-lg"></div>
          )}
        </div>
        <div className="px-14">
          <div className="mb-9">
            <p className="light-blue regular">
              Please remember that all bank accounts have to be assigned to your
              personal details.
            </p>
          </div>

          {/* Bank Account List */}
          <div
            className={`${
              isBitramp()
                ? "border-t border-[#464646]"
                : "border-t border-gray-200"
            }`}
          >
            {bankAccounts?.data?.map((account) => (
              <div
                key={account.id}
                className={`${
                  isBitramp()
                    ? "flex flex-col items-center py-4 border-b border-[#464646]"
                    : "flex flex-col items-center py-4 border-b border-gray-200"
                }`}
              >
                <div className="flex items-center w-full ">
                  <div className="light-blue medium w-[18rem] flex justify-start items-center gap-1">
                    {account?.status?.status === "pending" ? (
                      <svg
                        width="18"
                        height="16"
                        viewBox="0 0 18 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.91901 0.00690456C9.25542 -0.0401983 9.60048 0.160234 9.80979 0.408996L17.9675 14.8654C18.0949 15.2501 17.8382 15.9647 17.3702 15.9998H0.604801C0.262459 15.9664 -0.0320001 15.3637 0.00280185 15.0568C0.0131684 14.9648 0.0615455 14.8713 0.104246 14.7903L8.23901 0.384709C8.35699 0.20979 8.71143 0.0360985 8.91901 0.00690456ZM8.74648 4.19611C8.34119 4.25205 7.76832 4.63108 7.71896 5.06138C7.69872 5.239 7.7392 5.41048 7.74364 5.57387C7.78831 7.26663 7.93616 8.94786 8.1114 10.6225C8.13584 10.8553 8.11486 11.0464 8.23087 11.2633C8.51669 11.7978 9.23568 11.8285 9.60246 11.3675C9.79868 11.121 9.75869 10.8224 9.78955 10.5246C9.95516 8.93093 10.1193 7.25485 10.1593 5.64943C10.1667 5.35038 10.2099 4.97331 10.0213 4.73117C9.72167 4.34601 9.23839 4.12865 8.74623 4.19636L8.74648 4.19611ZM8.86989 12.771C8.31108 12.8294 7.78683 13.2747 8.01909 13.8745C8.31034 14.6267 9.70958 14.6054 9.94924 13.8193C10.1489 13.1645 9.46646 12.7087 8.86989 12.771Z"
                          fill="#FF0000"
                        />
                      </svg>
                    ) : null}
                    {account.name}
                  </div>
                  <div className="light-blue medium w-20">
                    {account.currency}
                  </div>
                  <div className="light-blue medium w-20"></div>
                  <div
                    className="light-blue medium flex-1 cursor-pointer"
                    title={account.iban}
                  >
                    {account.iban.slice(0, 4)}....{account.iban.slice(-4)}
                  </div>
                  <div className="flex-1 ps-4 light-blue medium">
                    {account.status.status}
                  </div>
                  {user?.is_staff && (
                    <button
                      className="Primary_button mr-4"
                      onClick={() => {
                        setShowEditBankForm(account.id);

                        populateEditBankData(account);
                      }}
                    >
                      Edit
                    </button>
                  )}
                  <button
                    className={
                      isBitramp()
                        ? "Delete_button_bitramp"
                        : "Outline_red_button"
                    }
                    onClick={() => {
                      setShowDeleteModal(true),
                        setIdForMutate(account.id),
                        setActionForModal("deleteBank");
                    }}
                  >
                    Delete
                  </button>
                </div>
                {user?.is_staff == false &&
                  account.status.decline_reason != "" && (
                    <textarea
                      className="w-full border border-gray-200 rounded-md p-2 text-gray-600 mt-4"
                      placeholder="Admin notes or comments"
                      rows={3}
                      value={account.status.decline_reason}
                      disabled={isDisabled || !user?.is_superuser}
                    />
                  )}
                {showEditBankForm == account.id && !isDisabled && (
                  <div
                    className={`w-full bank__add__container Container ${
                      isBitramp() ? "!bg-[#313131]" : ""
                    }  mt-4`}
                  >
                    <div className="flex justify-between items-center">
                      <h3 className="p-5 Subtitle semi-bold dark-blue w-full ml-5">
                        Edit Status
                      </h3>

                      <button
                        className="text-gray-400 hover:text-gray-600 mr-9 cursor-pointer"
                        onClick={() => setShowEditBankForm(null)}
                      >
                        <X size={18} />
                      </button>
                    </div>
                    <div className="flex justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="95%"
                        height="2"
                        viewBox="0 0 1166 2"
                        fill="none"
                      >
                        <path
                          d="M1 1L1165 0.999898"
                          stroke={isBitramp() ? "#464646" : "#E0E0E0"}
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <div className="input__group py-6 px-8 d-flex items-center gap-10 mb-2">
                      <div className="flex items-center gap-10 mb-2">
                        <label className="Text light-blue medium whitespace-nowrap w-[30%]">
                          Account Name
                        </label>
                        <input
                          type="text"
                          style={{
                            borderRadius: "10px",
                            background: isBitramp() ? "#2b2b2b" : "#fff",
                            border: "0.5px solid #D9D9D9",
                            width: "90%",
                          }}
                          className={`${
                            isBitramp() ? "placeholder-[#464646]" : ""
                          } Input `}
                          placeholder="Debit Card"
                          value={editedBankAccount.name}
                          onChange={(e) =>
                            setEditedBankAccount({
                              ...editedBankAccount,
                              name: e.target.value,
                            })
                          }
                          required
                          disabled={isDisabled || !user?.is_superuser}
                        />
                      </div>

                      <div className="flex items-center gap-10 mb-2">
                        <label className="Text light-blue medium whitespace-nowrap w-[30%]">
                          Account Owner
                        </label>
                        <input
                          type="text"
                          style={{
                            borderRadius: "10px",
                            background: isBitramp() ? "#2b2b2b" : "#fff",
                            border: "0.5px solid #D9D9D9",
                            width: "90%",
                          }}
                          className="Input"
                          placeholder="Albert John"
                          value={editedBankAccount.owner_name}
                          onChange={(e) =>
                            setEditedBankAccount({
                              ...editedBankAccount,
                              owner_name: e.target.value,
                            })
                          }
                          required
                          disabled={isDisabled || !user?.is_superuser}
                        />
                      </div>

                      <div className="flex items-center gap-10 mb-2">
                        <label className="Text light-blue medium whitespace-nowrap w-[30%]">
                          Account Number (IBAN)
                        </label>
                        <input
                          type="text"
                          style={{
                            borderRadius: "10px",
                            background: isBitramp() ? "#2b2b2b" : "#fff",
                            border: "0.5px solid #D9D9D9",
                            width: "90%",
                          }}
                          className={`${
                            isBitramp() ? "placeholder-[#464646]" : ""
                          } Input `}
                          placeholder="ROOOBRDE 1267 8987 6453 2145"
                          value={editedBankAccount.iban}
                          onChange={(e) =>
                            setEditedBankAccount({
                              ...editedBankAccount,
                              iban: e.target.value,
                            })
                          }
                          required
                          disabled={isDisabled || !user?.is_superuser}
                        />
                      </div>

                      <div className="flex items-center gap-10 mb-2">
                        <label className="Text light-blue medium whitespace-nowrap w-[30%]">
                          Bank Name
                        </label>
                        <input
                          type="text"
                          style={{
                            borderRadius: "10px",
                            background: isBitramp() ? "#2b2b2b" : "#fff",
                            border: "0.5px solid #D9D9D9",
                            width: "90%",
                          }}
                          className="Input"
                          placeholder="Bank Name"
                          value={editedBankAccount.bank_name}
                          onChange={(e) =>
                            setEditedBankAccount({
                              ...editedBankAccount,
                              bank_name: e.target.value,
                            })
                          }
                          required
                          disabled={isDisabled || !user?.is_superuser}
                        />
                      </div>

                      <div className="flex items-center gap-10 mb-2">
                        <label className="Text light-blue medium whitespace-nowrap w-[30%]">
                          SWIFT Code
                        </label>
                        <input
                          type="text"
                          style={{
                            borderRadius: "10px",
                            background: isBitramp() ? "#2b2b2b" : "#fff",
                            border: "0.5px solid #D9D9D9",
                            width: "90%",
                          }}
                          className="Input"
                          placeholder="SWIFT Code"
                          value={editedBankAccount.swift}
                          onChange={(e) =>
                            setEditedBankAccount({
                              ...editedBankAccount,
                              swift: e.target.value,
                            })
                          }
                          required
                          disabled={isDisabled || !user?.is_superuser}
                        />
                      </div>

                      <div className="flex items-center gap-10 mb-2">
                        <label
                          className="Text light-blue medium whitespace-nowrap w-[30%]"
                          style={{ width: "24%" }}
                        >
                          Currency
                        </label>
                        <div className="relative">
                          <select
                            className={`${
                              isBitramp()
                                ? "bg-[#212121] rounded-[0.625rem] border-[#2B2B2B] text-white focus:outline-none focus:ring-0 focus:border-[rgba(255,255,255,0.1)]"
                                : ""
                            }currency`}
                            value={editedBankAccount.currency}
                            onChange={(e) =>
                              setEditedBankAccount({
                                ...editedBankAccount,
                                currency: e.target.value,
                              })
                            }
                            disabled={isDisabled || !user?.is_superuser}
                          >
                            <option value="EUR">EUR</option>
                            <option value="USD">USD</option>
                            <option value="PLN">PLN</option>
                          </select>
                        </div>
                      </div>

                      <form encType="multipart/form-data">
                        <div className="flex flex-col gap-5 mt-8 ">
                          <div className="flex items-center gap-3">
                            <label
                              htmlFor="statement-upload"
                              className={`${
                                isBitramp()
                                  ? "Delete_button_bitramp"
                                  : " Outline_red_button_active"
                              }`}
                            >
                              Delete statement
                            </label>
                            <input
                              id="statement-upload"
                              className="hidden"
                              type="file"
                              // onChange={handleStatementChange}
                            />
                            {account.statement && (
                              <a
                                href={account.statement || undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#B4B4B4] underline hover:text-[#999]"
                              >
                                {getFileName(account.statement)}
                              </a>
                            )}
                            {/* <span className="text-[#B4B4B4]">
                              {getFileName(account.statement)}
                            </span> */}
                          </div>
                        </div>
                      </form>

                      <textarea
                        className="w-full border border-gray-200 rounded-md p-2  mt-4 Input"
                        placeholder="Admin notes or comments"
                        rows={3}
                        value={
                          editBankStatus.decline_reason != ""
                            ? editBankStatus.decline_reason
                            : account.status.decline_reason
                        }
                        onChange={(e) =>
                          setEditBankStatus((prev) => ({
                            ...prev,
                            decline_reason: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="mt-3 ml-7">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="97%"
                        height="2"
                        viewBox="0 0 1166 2"
                        fill="none"
                      >
                        <path
                          d="M1 1L1165 0.999898"
                          stroke={isBitramp() ? "#464646" : "#E0E0E0"}
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <div className="flex justify-end mt-7 mb-5">
                      {user?.is_superuser ? (
                        <Button
                          onClick={() => {
                            editBankAccountMutation.mutate({
                              bank_name: editedBankAccount.bank_name,
                              iban: editedBankAccount.iban,
                              swift: editedBankAccount.swift,
                              currency: editedBankAccount.currency,
                              id: editedBankAccount.id,
                              owner_name: editedBankAccount.owner_name,
                            });
                          }}
                          type="button"
                          className="Primary_button mr-2"
                        >
                          Save
                        </Button>
                      ) : null}
                      <button
                        className="Approve_button"
                        onClick={() => {
                          setShowConfirmModal(true),
                            setActionForModal("statusBank"),
                            setEditBankStatus((prev) => ({
                              ...prev,
                              status: "approved",
                              wallet_id: account.id,
                            }));
                        }}
                      >
                        Approve
                      </button>
                      <button
                        className={`${
                          isBitramp() ? "Decline_button" : "Outline_red_button"
                        }  ml-3 mr-3`}
                        onClick={() => {
                          setShowConfirmModal(true),
                            setActionForModal("statusBank"),
                            setEditBankStatus((prev) => ({
                              ...prev,
                              status: "declined",
                              wallet_id: account.id,
                            }));
                        }}
                      >
                        Decline
                      </button>
                      <button
                        className={`${
                          isBitramp()
                            ? "Delete_button_bitramp"
                            : "Outline_red_button_active"
                        }  mr-7`}
                        onClick={() => {
                          setShowConfirmModal(true),
                            setActionForModal("deleteBank"),
                            setIdForMutate(account.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add Bank Account Button */}
          {!showAddBankForm && (!user?.is_staff || user.is_superuser) && (
            <button
              className={`flex Text bold dark-gray bank__add mt-[1rem] p-4 cursor-pointer text-left mb-4 ${
                isBitramp() ? "bg-[#313131]" : ""
              }`}
              onClick={() => setShowAddBankForm(true)}
            >
              <span
                className={`${isBitramp() ? "text-[#8AFEFD] mr-2" : "mr-2"}`}
              >
                +
              </span>{" "}
              <div className={`${isBitramp() ? "text-[#8AFEFD]" : ""}`}>
                Add Bank Account
              </div>
            </button>
          )}

          {/* Add Bank Account Form */}
          {showAddBankForm && (!user?.is_staff || user.is_superuser) && (
            <div
              className={`${
                isBitramp()
                  ? "w-full bank__add__container mt-4 mb-4 bg-[#313131]"
                  : "w-full bank__add__container mt-4 mb-4"
              }`}
            >
              <div className="flex justify-between items-center">
                <div
                  className={`${
                    isBitramp()
                      ? "text-white p-5 Text bold w-full ml-5"
                      : "p-5 Text bold w-full ml-5"
                  }`}
                >
                  Add
                </div>

                <button
                  className="text-gray-400 hover:text-gray-600 mr-9 cursor-pointer"
                  onClick={() => setShowAddBankForm(false)}
                >
                  <X size={18} />
                </button>
              </div>
              <div className="flex justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="95%"
                  height="2"
                  viewBox="0 0 1166 2"
                  fill="none"
                >
                  <path
                    d="M1 1L1165 0.999898"
                    stroke={isBitramp() ? "#464646" : "#E0E0E0"}
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="input__group py-6 px-8 d-flex items-center gap-10 mb-2">
                <div className="flex items-center gap-10 mb-2">
                  <label className="Text light-blue medium whitespace-nowrap w-[30%]">
                    Personalized Account Name
                  </label>
                  <input
                    type="text"
                    style={{
                      borderRadius: "10px",
                      background: isBitramp() ? "#212121" : "#FFF",
                      border: "0.5px solid #D9D9D9",
                      width: "90%",
                    }}
                    className={`Input ${
                      isBitramp() ? "placeholder:text-[#464646]" : ""
                    } `}
                    placeholder="Debit Card"
                    value={newBankAccount.name}
                    required
                    onChange={(e) =>
                      setNewBankAccount({
                        ...newBankAccount,
                        name: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="flex items-center gap-10 mb-2">
                  <label className="Text light-blue medium whitespace-nowrap w-[30%]">
                    Account Owner
                  </label>
                  <input
                    type="text"
                    style={{
                      borderRadius: "10px",
                      background: isBitramp() ? "#212121" : "#FFF",
                      border: "0.5px solid #D9D9D9",
                      width: "90%",
                    }}
                    className={`Input ${
                      isBitramp() ? "placeholder:text-[#464646]" : ""
                    } `}
                    placeholder="Albert John"
                    value={newBankAccount.owner_name}
                    required
                    onChange={(e) =>
                      setNewBankAccount({
                        ...newBankAccount,
                        owner_name: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="flex items-center gap-10 mb-2">
                  <label className="Text light-blue medium whitespace-nowrap w-[30%]">
                    Account Number (IBAN)
                  </label>
                  <input
                    type="text"
                    style={{
                      borderRadius: "10px",
                      background: isBitramp() ? "#212121" : "#FFF",
                      border: "0.5px solid #D9D9D9",
                      width: "90%",
                    }}
                    className={`Input ${
                      isBitramp() ? "placeholder:text-[#464646]" : ""
                    } `}
                    placeholder="ROOOBRDE 1267 8987 6453 2145"
                    value={newBankAccount.iban}
                    required
                    onChange={(e) =>
                      setNewBankAccount({
                        ...newBankAccount,
                        iban: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="flex items-center gap-10 mb-2">
                  <label className="Text light-blue medium whitespace-nowrap w-[30%]">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    style={{
                      borderRadius: "10px",
                      background: isBitramp() ? "#212121" : "#FFF",
                      border: "0.5px solid #D9D9D9",
                      width: "90%",
                    }}
                    className={`Input ${
                      isBitramp() ? "placeholder:text-[#464646]" : ""
                    } `}
                    placeholder="Bank Name"
                    value={newBankAccount.bank_name}
                    required
                    onChange={(e) =>
                      setNewBankAccount({
                        ...newBankAccount,
                        bank_name: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="flex items-center gap-10 mb-2">
                  <label className="Text light-blue medium whitespace-nowrap w-[30%]">
                    SWIFT Code
                  </label>
                  <input
                    type="text"
                    style={{
                      borderRadius: "10px",
                      background: isBitramp() ? "#212121" : "#FFF",
                      border: "0.5px solid #D9D9D9",
                      width: "90%",
                    }}
                    className={`Input ${
                      isBitramp() ? "placeholder:text-[#464646]" : ""
                    } `}
                    placeholder="SWIFT Code"
                    value={newBankAccount.swift}
                    required
                    onChange={(e) =>
                      setNewBankAccount({
                        ...newBankAccount,
                        swift: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="flex items-center gap-10 mb-2">
                  <label
                    className="Text light-blue medium whitespace-nowrap w-[30%]"
                    style={{ width: "24%" }}
                  >
                    Currency
                  </label>
                  <div className="relative">
                    <div className="relative w-full" ref={currencyRef}>
                      <div
                        onClick={() =>
                          setCurrencyDropdownOpen(!currencyDropdownOpen)
                        }
                        className={`${
                          isBitramp()
                            ? "Input px-4 py-[0.45rem] mt-1 rounded-[0.625rem] !bg-[#464646] border cursor-pointer flex items-center justify-between gap-2"
                            : "Input px-4 py-[0.45rem] mt-1 rounded-[0.625rem] bg-white border cursor-pointer flex items-center justify-between gap-2"
                        }`}
                      >
                        {newBankAccount.currency || "Select Currency"}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="11"
                          height="10"
                          viewBox="0 0 11 10"
                          fill="none"
                        >
                          <path
                            d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                            fill={isBitramp() ? "#fff" : "#737791"}
                          />
                        </svg>
                      </div>

                      <AnimatePresence>
                        {currencyDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.2 }}
                            className={`${
                              isBitramp()
                                ? "absolute top-full left-0 w-full bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] crypto-list_bitramp text-white rounded-[0.625rem] shadow-md mt-1 z-10"
                                : "absolute top-full left-0 w-full bg-white rounded-[0.625rem] shadow-md mt-1 z-10"
                            }`}
                          >
                            <div
                              onClick={() => handleCurrencySelect("EUR")}
                              className={`${
                                isBitramp()
                                  ? "px-4 py-2 hover:bg-[#262626] cursor-pointer rounded-t-[0.625rem]"
                                  : "px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              }`}
                            >
                              EUR
                            </div>
                            <div
                              onClick={() => handleCurrencySelect("USD")}
                              className={`${
                                isBitramp()
                                  ? "px-4 py-2 hover:bg-[#262626] cursor-pointer"
                                  : "px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              }`}
                            >
                              USD
                            </div>
                            <div
                              onClick={() => handleCurrencySelect("PLN")}
                              className={`${
                                isBitramp()
                                  ? "px-4 py-2 hover:bg-[#262626] cursor-pointer rounded-b-[0.625rem]"
                                  : "px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              }`}
                            >
                              PLN
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>

                <form encType="multipart/form-data">
                  <div className="flex flex-col gap-5 mt-8 ">
                    <p
                      className={`${
                        isBitramp() ? "text-[#8AFEFD]" : "text-yellow-300"
                      }`}
                    >
                      Statement must be newly issued and not older than 3
                      months.
                    </p>
                    <div className="flex items-center gap-3">
                      <label
                        htmlFor="statement-upload"
                        className={
                          isBitramp()
                            ? "Upload_button_bitramp"
                            : "Outline_button"
                        }
                      >
                        Upload Statement
                      </label>
                      <input
                        id="statement-upload"
                        className="hidden"
                        type="file"
                        onChange={handleStatementChange}
                        required
                      />
                      {fileName && (
                        <span className="text-[#B4B4B4]">{fileName}</span>
                      )}
                    </div>
                  </div>
                </form>
              </div>
              <div className="mt-3 ml-7">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="97%"
                  height="2"
                  viewBox="0 0 1166 2"
                  fill="none"
                >
                  <path
                    d="M1 1L1165 0.999898"
                    stroke={isBitramp() ? "#464646" : "#E0E0E0"}
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="mt-7 pb-5 ml-7">
                <button
                  className=" Primary_button"
                  onClick={() => {
                    setShowConfirmModal(true),
                      setActionForModal("bank"),
                      setIdForMutate(null);
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Crypto Wallet Section */}
      <div className="mt-10">
        <div className="flex gap-7 items-center mb-10">
          <div
            className={`w-fit whitespace-nowrap semi-bold ${
              isBitramp() ? "text-white" : ""
            }`}
          >
            Crypto Wallet
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="75%"
            height="2"
            viewBox="0 0 1024 2"
            fill="none"
          >
            <path
              d="M1 1L1023 1.00009"
              stroke={isBitramp() ? "#464646" : "#E0E0E0"}
              stroke-width="2"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <div className="px-14">
          <div className="mb-9">
            <p className="light-blue regular">
              Please remember that all crypto wallets have to be assigned to
              your personal details.
            </p>
          </div>

          {/* Crypto Wallet List */}
          <div
            className={`${
              isBitramp()
                ? "border-t border-[#464646]"
                : "border-t border-gray-200"
            }`}
          >
            {cryptoAccounts?.data?.map((wallet) => (
              <div>
                <div
                  key={wallet.id}
                  className={`${
                    isBitramp()
                      ? "py-4 border-b border-[#464646]"
                      : "py-4 border-b border-gray-200"
                  }`}
                >
                  <div className="flex items-center mb-2">
                    <div className="light-blue medium w-[25rem] flex justify-start items-center gap-1">
                      {wallet?.status?.status === "pending" ? (
                        <svg
                          width="18"
                          height="16"
                          viewBox="0 0 18 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8.91901 0.00690456C9.25542 -0.0401983 9.60048 0.160234 9.80979 0.408996L17.9675 14.8654C18.0949 15.2501 17.8382 15.9647 17.3702 15.9998H0.604801C0.262459 15.9664 -0.0320001 15.3637 0.00280185 15.0568C0.0131684 14.9648 0.0615455 14.8713 0.104246 14.7903L8.23901 0.384709C8.35699 0.20979 8.71143 0.0360985 8.91901 0.00690456ZM8.74648 4.19611C8.34119 4.25205 7.76832 4.63108 7.71896 5.06138C7.69872 5.239 7.7392 5.41048 7.74364 5.57387C7.78831 7.26663 7.93616 8.94786 8.1114 10.6225C8.13584 10.8553 8.11486 11.0464 8.23087 11.2633C8.51669 11.7978 9.23568 11.8285 9.60246 11.3675C9.79868 11.121 9.75869 10.8224 9.78955 10.5246C9.95516 8.93093 10.1193 7.25485 10.1593 5.64943C10.1667 5.35038 10.2099 4.97331 10.0213 4.73117C9.72167 4.34601 9.23839 4.12865 8.74623 4.19636L8.74648 4.19611ZM8.86989 12.771C8.31108 12.8294 7.78683 13.2747 8.01909 13.8745C8.31034 14.6267 9.70958 14.6054 9.94924 13.8193C10.1489 13.1645 9.46646 12.7087 8.86989 12.771Z"
                            fill="#FF0000"
                          />
                        </svg>
                      ) : null}
                      {wallet.name}
                    </div>
                    <div className="light-blue medium w-[6rem]">
                      {wallet.currency}
                    </div>
                    <div className="w-20 light-blue medium">
                      {wallet.network}
                    </div>
                    <div
                      className="light-blue medium w-[15rem] flex justify-center gap-2 items-center cursor-pointer"
                      title={wallet.wallet_address}
                    >
                      {wallet.wallet_address.slice(0, 4)}....
                      {wallet.wallet_address.slice(-4)}
                    </div>
                    <div className="flex-1 light-blue medium mr-[1rem]">
                      {wallet.status.status}
                    </div>
                    {user?.is_staff && (
                      <button
                        className="Primary_button mr-4"
                        onClick={() => {
                          setShowEditCryptoForm(wallet.id);

                          populateEditCryptoData(wallet);
                        }}
                      >
                        Edit
                      </button>
                    )}
                    <button
                      className={
                        isBitramp()
                          ? "Delete_button_bitramp"
                          : "Outline_red_button"
                      }
                      onClick={() => {
                        setShowDeleteModal(true),
                          setIdForMutate(wallet.id),
                          setActionForModal("deleteCrypto");
                      }}
                    >
                      Delete
                    </button>
                    
                  </div>
                  {wallet.status.status == "declined" && (
                    <textarea
                      className="w-full border border-gray-200 rounded-md p-2 text-gray-600"
                      placeholder="Admin notes or comments"
                      rows={3}
                      value={wallet.status.decline_reason}
                      disabled={isDisabled || !user?.is_superuser}
                    />
                  )}
                </div>
                {showEditCryptoForm == wallet.id && !isDisabled && (
                  <div
                    className={`${
                      isBitramp() ? "!bg-[#313131]" : ""
                    } w-full bank__add__container Container mt-4`}
                  >
                    <div className="flex justify-between items-center">
                      <div
                        className={`${
                          isBitramp() ? "text-white" : ""
                        } p-5 add__title w-full ml-5`}
                      >
                        Add
                      </div>
                      <button
                        className={`${
                          isBitramp() ? "text-white" : ""
                        } mr-9 cursor-pointer`}
                        onClick={() => setShowEditCryptoForm(null)}
                      >
                        <X size={18} />
                      </button>
                    </div>
                    <div className="flex justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="95%"
                        height="2"
                        viewBox="0 0 1166 2"
                        fill="none"
                      >
                        <path
                          d="M1 1L1165 0.999898"
                          stroke={isBitramp() ? "#464646" : "#E0E0E0"}
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <div className="input__group py-6 px-8 d-flex items-center gap-10 mb-2">
                      <div className="flex items-center gap-10 mb-2">
                        <label className="Text light-blue medium whitespace-nowrap w-[30%]">
                          Wallet Name
                        </label>
                        <input
                          type="text"
                          style={{
                            borderRadius: "10px",
                            background: isBitramp() ? "#212121" : "#FFF",
                            border: "0.5px solid #D9D9D9",
                            width: "90%",
                          }}
                          className="Input placeholder:text-[#464646]"
                          value={editedCryptoAccountData.name}
                          onChange={(e) =>
                            setEditedCryptoData({
                              ...editedCryptoAccountData,
                              name: e.target.value,
                            })
                          }
                          required
                          disabled={isDisabled || !user?.is_superuser}
                        />
                      </div>

                      <div className="flex items-center gap-10 mb-2">
                        <label className="Text light-blue medium whitespace-nowrap w-[30%]">
                          Wallet Address
                        </label>
                        <input
                          type="text"
                          style={{
                            borderRadius: "10px",
                            background: isBitramp() ? "#212121" : "#FFF",
                            border: "0.5px solid #D9D9D9",
                            width: "90%",
                          }}
                          className="Input placeholder:text-[#464646]"
                          value={editedCryptoAccountData.wallet_address}
                          onChange={(e) =>
                            setEditedCryptoData({
                              ...editedCryptoAccountData,
                              wallet_address: e.target.value,
                            })
                          }
                          required
                          disabled={isDisabled || !user?.is_superuser}
                        />
                      </div>

                      <div className="flex items-center gap-10 mb-2">
                        <label
                          className="Text light-blue medium whitespace-nowrap w-[30%]"
                          style={{ width: "24%" }}
                        >
                          Network
                        </label>
                        <div className="relative w-32">
                          <select
                            className={`${
                              isBitramp()
                                ? "bg-[#212121] border-[#2B2B2B] text-white focus:outline-none focus:ring-0 focus:border-[rgba(255,255,255,0.1)]"
                                : ""
                            } currency`}
                            value={editedCryptoAccountData.network}
                            onChange={(e) =>
                              setEditedCryptoData({
                                ...editedCryptoAccountData,
                                network: e.target.value,
                              })
                            }
                          >
                            {NETWORK_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="flex items-center gap-10 mb-2">
                        <label
                          className="Text light-blue medium whitespace-nowrap w-[30%]"
                          style={{ width: "24%" }}
                        >
                          Currency
                        </label>
                        <div className="relative w-32">
                          <select
                            className={`${
                              isBitramp()
                                ? "bg-[#212121] rounded-[0.625rem] border-[#2B2B2B] text-white focus:outline-none focus:ring-0 focus:border-[rgba(255,255,255,0.1)]"
                                : ""
                            } currency`}
                            value={editedCryptoAccountData.currency}
                            onChange={(e) =>
                              setEditedCryptoData({
                                ...editedCryptoAccountData,
                                currency: e.target.value,
                              })
                            }
                          >
                            <option value="ETH">ETH</option>
                            <option value="BTC">BTC</option>
                            <option value="USDT">USDT</option>
                            <option value="USDC">USDC</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end mt-7 pb-4">
                    {user?.is_superuser ? (
                        <Button
                          onClick={() => {
                            editCryptoAccountMutation.mutate({
                              name: editedCryptoAccountData.name,
                              wallet_address:
                                editedCryptoAccountData.wallet_address,
                              network: editedCryptoAccountData.network,
                              currency: editedCryptoAccountData.currency,
                              id: editedCryptoAccountData.id,
                            });
                          }}
                          type="button"
                          className="Primary_button mr-2"
                        >
                          Save
                        </Button>
                      ) : null}
                      <button
                        className="Approve_button"
                        onClick={() => {
                          setShowConfirmModal(true),
                            setActionForModal("statusCrypto"),
                            setEditBankStatus((prev) => ({
                              ...prev,
                              status: "approved",
                              wallet_id: wallet.id,
                            }));
                        }}
                      >
                        Approve
                      </button>
                      <button
                        className={`${
                          isBitramp() ? "Decline_button" : "Outline_red_button"
                        }  ml-3 mr-3`}
                        onClick={() => {
                          setShowConfirmModal(true),
                            setActionForModal("statusCrypto"),
                            setEditBankStatus((prev) => ({
                              ...prev,
                              status: "declined",
                              wallet_id: wallet.id,
                            }));
                        }}
                      >
                        Decline
                      </button>
                      {/* <button
                        className={`${
                          isBitramp()
                            ? "Delete_button_bitramp"
                            : "Outline_red_button "
                        } mr-4`}
                        onClick={() => {
                          setShowConfirmModal(true),
                            setActionForModal("deleteCrypto"),
                            setIdForMutate(wallet.id);
                        }}
                      >
                        Delete
                      </button> */}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add Crypto Wallet Button */}
          {!showAddCryptoForm && (!user?.is_staff || user.is_superuser) && (
            <button
              className={`${
                isBitramp()
                  ? "flex Text bold dark-gray bg-[#313131] bank__add mt-[1rem] p-4 cursor-pointer text-left"
                  : "flex Text bold dark-gray bank__add mt-[1rem] p-4 cursor-pointer text-left"
              }`}
              onClick={() => setShowAddCryptoForm(true)}
            >
              <span
                className={`${isBitramp() ? "text-[#8AFEFD] mr-2" : "mr-2"}`}
              >
                +
              </span>{" "}
              <div className={`${isBitramp() ? "text-[#8AFEFD]" : ""}`}>
                Add Crypto Wallet
              </div>
            </button>
          )}

          {/* Add Crypto Wallet Form */}
          {showAddCryptoForm && (
            <div
              className={`${
                isBitramp()
                  ? "w-full bank__add__container mt-4 mb-4 bg-[#313131]"
                  : "w-full bank__add__container mt-4 mb-4"
              }`}
            >
              <div className="flex justify-between items-center">
                <div
                  className={`${
                    isBitramp()
                      ? "p-5 Text bold w-full ml-5 text-[white]"
                      : "p-5 Text bold w-full ml-5"
                  }`}
                >
                  Add
                </div>
                <button
                  className="text-gray-400 hover:text-gray-600 mr-9 cursor-pointer"
                  onClick={() => setShowAddCryptoForm(false)}
                >
                  <X size={18} />
                </button>
              </div>
              <div className="flex justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="95%"
                  height="2"
                  viewBox="0 0 1166 2"
                  fill="none"
                >
                  <path
                    d="M1 1L1165 0.999898"
                    stroke={isBitramp() ? "#464646" : "#E0E0E0"}
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="input__group py-6 px-8 d-flex items-center gap-10 mb-2">
                <div className="flex items-center gap-10 mb-2">
                  <label className="Text light-blue medium whitespace-nowrap w-[30%]">
                    Wallet Name
                  </label>
                  <input
                    type="text"
                    style={{
                      borderRadius: "10px",
                      background: isBitramp() ? "#212121" : "#FFF",
                      border: "0.5px solid #D9D9D9",
                      width: "90%",
                    }}
                    className={`Input ${
                      isBitramp() ? "placeholder:text-[#464646]" : ""
                    } `}
                    value={newCryptoWallet.name}
                    required
                    onChange={(e) =>
                      setNewCryptoWallet({
                        ...newCryptoWallet,
                        name: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="flex items-center gap-10 mb-2">
                  <label className="Text light-blue medium whitespace-nowrap w-[30%]">
                    Wallet Address
                  </label>
                  <input
                    type="text"
                    style={{
                      borderRadius: "10px",
                      background: isBitramp() ? "#212121" : "#FFF",
                      border: "0.5px solid #D9D9D9",
                      width: "90%",
                    }}
                    className={`Input ${
                      isBitramp() ? "placeholder:text-[#464646]" : ""
                    } `}
                    placeholder={walletAddressExample}
                    value={newCryptoWallet.wallet_address}
                    required
                    onChange={(e) =>
                      setNewCryptoWallet({
                        ...newCryptoWallet,
                        wallet_address: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="flex items-center gap-10 mb-2">
                  <label
                    className="Text light-blue medium whitespace-nowrap w-[30%]"
                    style={{ width: "24%" }}
                  >
                    Network
                  </label>
                  <div className="relative w-32" ref={networkRef}>
                    <div className="relative w-full">
                      <div
                        onClick={() =>
                          setNetworkDropdownOpen(!networkDropdownOpen)
                        }
                        className={`${
                          isBitramp()
                            ? "Input px-4 py-[0.45rem] mt-1 rounded-[0.625rem] !bg-[#464646] border cursor-pointer flex items-center justify-between gap-2"
                            : "Input px-4 py-[0.45rem] mt-1 rounded-[0.625rem] bg-white border cursor-pointer flex items-center justify-between gap-2"
                        }`}
                      >
                        {NETWORK_OPTIONS.find(
                          (option) => option.value === newCryptoWallet.network
                        )?.label || "Select Network"}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="11"
                          height="10"
                          viewBox="0 0 11 10"
                          fill="none"
                        >
                          <path
                            d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.5C10.2143 0.5 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                            fill={isBitramp() ? "#fff" : "#737791"}
                          />
                        </svg>
                      </div>

                      <AnimatePresence>
                        {networkDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.2 }}
                            className={`${
                              isBitramp()
                                ? "absolute top-full left-0 w-full bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] crypto-list_bitramp text-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                                : "absolute top-full left-0 w-full bg-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                            }`}
                          >
                            {NETWORK_OPTIONS.map((option) => (
                              <div
                                key={option.value}
                                onClick={() =>
                                  handleNetworkSelect(option.value)
                                }
                                className={`${
                                  isBitramp()
                                    ? "px-4 py-2 hover:bg-[#262626] cursor-pointer rounded-t-[0.625rem]"
                                    : "px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                }`}
                              >
                                {option.label}
                              </div>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-10 mb-2">
                  <label
                    className="Text light-blue medium whitespace-nowrap w-[30%]"
                    style={{ width: "24%" }}
                  >
                    Currency
                  </label>
                  <div className="relative w-32" ref={currencyCryptoRef}>
                    <div className="relative w-full">
                      <div
                        onClick={() =>
                          setCurrencyCryptoDropdownOpen(
                            !currencyCryptoDropdownOpen
                          )
                        }
                        className={`${
                          isBitramp()
                            ? "Input px-4 py-[0.45rem] mt-1 rounded-[0.625rem] !bg-[#464646] border cursor-pointer flex items-center justify-between gap-2"
                            : "Input px-4 py-[0.45rem] mt-1 rounded-[0.625rem] bg-white border cursor-pointer flex items-center justify-between gap-2"
                        }`}
                      >
                        {newCryptoWallet.currency || "Select Currency"}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="11"
                          height="10"
                          viewBox="0 0 11 10"
                          fill="none"
                        >
                          <path
                            d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.5C10.2143 0.5 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                            fill={isBitramp() ? "#fff" : "#737791"}
                          />
                        </svg>
                      </div>

                      <AnimatePresence>
                        {currencyCryptoDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.2 }}
                            className={`${
                              isBitramp()
                                ? "absolute top-full left-0 w-full bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] crypto-list_bitramp text-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                                : "absolute top-full left-0 w-full bg-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                            }`}
                          >
                            {["ETH", "BTC", "USDT", "USDC"].map((currency) => (
                              <div
                                key={currency}
                                onClick={() =>
                                  handleCurrencyCryptoSelect(currency)
                                }
                                className={`${
                                  isBitramp()
                                    ? "px-4 py-2 hover:bg-[#262626] cursor-pointer rounded-t-[0.625rem]"
                                    : "px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                }`}
                              >
                                {currency}
                              </div>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-7 pb-5 ml-7">
                <button
                  className="Primary_button"
                  onClick={() => {
                    setActionForModal("crypto"),
                      setShowConfirmModal(true),
                      setIdForMutate(null);
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showConfirmModal && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[999999999] h-screen min-h-full"
          onClick={handleCloseModal} // Apelăm handleCloseModal când se face click pe fundal
        >
          <div
            className={`${
              isBitramp() ? "bg-[#2b2b2b]" : "bg-white"
            } min-w-[30rem] h-auto rounded-[1.5rem]  text-center relative`}
            onClick={(e) => e.stopPropagation()} // Prevenim propagarea evenimentului de click pe conținutul modalului
          >
            <button
              onClick={() => {
                setShowConfirmModal(false);
              }}
              className="absolute top-4 right-4"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
              >
                <path
                  d="M17 17L2 2"
                  stroke={isBitramp() ? "#fff" : "#202A43"}
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <path
                  d="M2 17L17 2"
                  stroke={isBitramp() ? "#fff" : "#202A43"}
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <div
              className={`${
                isBitramp() ? "text-white" : "text-[#05004E]"
              } text-center font-satoshi text-xl font-bold leading-7 pt-12`}
            >
              Please Confirm Your Details
            </div>
            <div className="text-center font-satoshi text-xl font-normal leading-7 light-blue pt-12 w-full flex justify-center items-center">
              I confirm that the information provided is
              <br /> correct and understand that any mistakes may
              <br /> affect future transactions
            </div>
            <div className="mt-7">
              <button
                className="Primary_button ml-[10rem] button__save__text mb-8"
                onClick={() => {
                  if (actionForModal === "bank") {
                    handleAddBankAccount();
                  } else if (actionForModal === "crypto") {
                    handleAddCryptoWallet();
                  } else if (
                    actionForModal === "statusBank" &&
                    editBankStatus.wallet_id !== 0
                  ) {
                    handleUpdateBankStatus(editBankStatus);
                  } else if (
                    actionForModal === "statusCrypto" &&
                    editBankStatus.wallet_id !== 0
                  ) {
                    handleUpdateCryptoStatus(editBankStatus);
                  }
                  setShowConfirmModal(false);
                }}
              >
                Accept & Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[999999999] h-screen min-h-full"
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            className={`${
              isBitramp() ? "bg-[#2b2b2b]" : "bg-white"
            } min-w-[30rem] h-auto rounded-[1.5rem] text-center relative`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                setShowDeleteModal(false);
              }}
              className="absolute top-4 right-4"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
              >
                <path
                  d="M17 17L2 2"
                  stroke={isBitramp() ? "#fff" : "#202A43"}
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <path
                  d="M2 17L17 2"
                  stroke={isBitramp() ? "#fff" : "#202A43"}
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <div
              className={`${
                isBitramp() ? "text-white" : "text-[#05004E]"
              } text-xl font-bold pt-12`}
            >
              Are you sure you want to delete?
            </div>
            <div className="mt-7 mb-8 flex gap-3 justify-center">
              <button
                className="Primary_button"
                onClick={() => {
                  if (actionForModal === "deleteBank" && idForMutate !== null) {
                    handleDeleteBankAccount(idForMutate);
                  } else if (
                    actionForModal === "deleteCrypto" &&
                    idForMutate !== null
                  ) {
                    handleDeleteCryptoWallet(idForMutate);
                  }
                  setShowDeleteModal(false);
                }}
              >
                Yes
              </button>
              <button
                className="Decline_button"
                onClick={() => setShowDeleteModal(false)}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankCryptoManagement;
